import ToColor from 'color';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';

import { useBackend } from 'dashboard/components/GraphModule/hooks/useBackend';
import { useGraphData } from 'dashboard/components/GraphModule/hooks/useGraphData';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import {
  ColorRule,
  DatumType,
  DatumTypeToken,
  NodeRenderConfig,
  PropertyConfig,
} from 'dashboard/models/graph';
import {
  ServerColorRule,
  ServerColorRuleCondition,
  ServerColorRuleStatus,
  ServerColorRuleTag,
  ServerNodeDatumType,
  ServerNodeRenderConfig,
  ServerPropertyConfig,
} from 'dashboard/models/graph-backend';
import { QueryConfigArgs } from 'dashboard/models/graph-query';
import {
  PERSONA_DATUM_TYPES,
  PERSONA_PROPERTY_DATUM_TYPES,
  TRANSACTIONS_DATUM_TYPE,
} from 'lib/constants/graph';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

export const GraphConfigurationContext = createContext<{
  queryConfigArgs: QueryConfigArgs;
  datumTypeTokens: string[];
  areGraphConfigsFetched: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
}>(null);

interface GraphConfigurationProviderProps {
  children: ReactNode;
}

const GraphConfigurationProvider = (props: GraphConfigurationProviderProps) => {
  const { accountTypes, accountStatuses, graph } = useGraphData();
  const transactionsDemoEnabled = useFeatureFlag('dashboard:graph-transactions-frontend');
  const { graphExplorer } = useGraphExplorer();
  const backend = useBackend();
  const [datumTypeTokens, setDatumTypeTokens] = useState<DatumTypeToken[]>(
    PERSONA_DATUM_TYPES.map((datumType) => datumType.token),
  );

  const formatGraphConfigTypes = (
    serverNodeDatumTypes: ServerNodeDatumType[],
    coloringRules: ServerColorRule[],
    nodeRenderConfigs: ServerNodeRenderConfig[],
    propertyConfigs: ServerPropertyConfig[],
  ): [
    nodeDatumTypes: DatumType[],
    formattedColoringRules: ColorRule[],
    nodeRenderConfigsByToken: Map<DatumTypeToken, NodeRenderConfig>,
    propertyConfigsByToken: PropertyConfig[],
  ] => {
    const nodeDatumTypes: DatumType[] = serverNodeDatumTypes.map((serverNodeDatumType) => {
      const datumType: DatumType = {
        token: serverNodeDatumType['datum-type-token'],
        isCustomType: true,
        name: serverNodeDatumType.name,
        valueType: serverNodeDatumType['value-type'],
      };
      return datumType;
    });
    const nodeRenderConfigsByToken: Map<DatumTypeToken, NodeRenderConfig> = new Map(
      nodeRenderConfigs.map((nodeRenderConfig) => {
        const renderConfig: NodeRenderConfig = {
          color: ToColor(nodeRenderConfig.color),
          icon: nodeRenderConfig.icon,
          displayName: nodeRenderConfig['display-name'],
          placeHolder: nodeRenderConfig['search-placeholder'],
          contextMenu: nodeRenderConfig['context-menu'],
        };
        return [nodeRenderConfig['datum-type-token'], renderConfig];
      }),
    );

    const propertyConfigsByToken: PropertyConfig[] = propertyConfigs.map((propertyConfig) => {
      const graphPropertyConfig: PropertyConfig = {
        token: propertyConfig['datum-type-token'],
        displayName: propertyConfig['display-name'],
        indexed: propertyConfig['indexed'],
        source: propertyConfig['property-source'],
        value: propertyConfig['property-value'],
        type: propertyConfig['property-type'],
      };
      return graphPropertyConfig;
    });

    const formattedColoringRules: ColorRule[] = (coloringRules || []).map((rule) => {
      let colorRule: ColorRule;
      switch (rule.type) {
        case 'tag': {
          colorRule = {
            color: ToColor(rule.color),
            type: rule.type,
            tag: (rule as ServerColorRuleTag).tag,
          };
          break;
        }
        case 'status': {
          colorRule = {
            color: ToColor(rule.color),
            type: rule.type,
            status: (rule as ServerColorRuleStatus).status,
          };
          break;
        }
        case 'condition': {
          colorRule = {
            color: ToColor(rule.color),
            condition: (rule as ServerColorRuleCondition).condition,
            type: rule.type,
          };
          break;
        }
      }
      return colorRule;
    });

    return [
      nodeDatumTypes,
      formattedColoringRules,
      nodeRenderConfigsByToken,
      propertyConfigsByToken,
    ];
  };

  const [areGraphConfigsFetched, setAreGraphConfigsFetched] = useState<boolean>(false);
  // Fetch custom datum types from the graph
  useEffect(() => {
    if (!graphExplorer || !backend || !graph) {
      return;
    }

    const nodeDatumTypesOverride = backend.customDatumTypesOverride();
    const nodeRenderConfigOverride = backend.nodeRenderConfigOverride();
    const coloringRulesOverride = backend.coloringRulesOverride();
    const propertyConfigOverride = backend.propertyConfigOverride();
    let nodeDatumTypesConfig: ServerNodeDatumType[];
    let coloringRules: ServerColorRule[] = [];
    let nodeRenderConfigs: ServerNodeRenderConfig[] = [];
    let propertyConfigs: ServerPropertyConfig[] = [];
    if (
      nodeDatumTypesOverride == null &&
      nodeRenderConfigOverride == null &&
      graph?.attributes &&
      propertyConfigOverride == null
    ) {
      nodeDatumTypesConfig = graph.attributes['node-types'];
      coloringRules = graph.attributes['coloring-rules'];
      nodeRenderConfigs = graph.attributes['node-render-configs'];
      propertyConfigs = graph.attributes['property-configs'];
    } else if (nodeDatumTypesOverride != null) {
      nodeDatumTypesConfig = nodeDatumTypesOverride;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nodeRenderConfigs = nodeRenderConfigOverride;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      coloringRules = coloringRulesOverride;
    } else {
      return;
    }
    const [
      nodeDatumTypes,
      formattedColoringRules,
      nodeRenderConfigsByToken,
      propertyConfigByToken,
    ] = formatGraphConfigTypes(
      nodeDatumTypesConfig,
      coloringRules,
      nodeRenderConfigs,
      propertyConfigs,
    );
    graphExplorer.setGraphConfiguration(
      nodeDatumTypes,
      formattedColoringRules,
      nodeRenderConfigsByToken,
      propertyConfigByToken,
    );
    setAreGraphConfigsFetched(true);

    const personaDatumTypeTokens = PERSONA_DATUM_TYPES.map((datumType) => datumType.token);
    const customDatumTypeTokens = graphExplorer
      .getNodeDatumTypes()
      .map((datumType) => datumType.token);
    setDatumTypeTokens([...personaDatumTypeTokens, ...customDatumTypeTokens]);
  }, [backend, graphExplorer, graph?.attributes]);

  const extraTransactionsDemoDatumTypes = useMemo(() => {
    return transactionsDemoEnabled ? [TRANSACTIONS_DATUM_TYPE] : [];
  }, [transactionsDemoEnabled]);

  const queryConfigArgs = {
    accountTypes: accountTypes,
    accountStatuses: accountStatuses,
    datumTypes: PERSONA_DATUM_TYPES,
    propertyDatumTypes: [...PERSONA_PROPERTY_DATUM_TYPES, ...extraTransactionsDemoDatumTypes],
    customDatumTypes: graphExplorer.getNodeDatumTypes() || [],
    graphPropertyConfigs: graphExplorer.getPropertyConfigs() || [],
  };

  if (areGraphConfigsFetched) {
    return (
      <GraphConfigurationContext.Provider
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value={{ queryConfigArgs, datumTypeTokens, areGraphConfigsFetched }}
      >
        {props.children}
      </GraphConfigurationContext.Provider>
    );
  } else {
    return null;
  }
};

export default GraphConfigurationProvider;
